// src/Components/ImportMarkers/DuplicateCheckOnImport.js

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';

import RichTextEditor from '../../utils/RichTextEditor';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { SoundWaveIcon } from '../../assets/icons/CustomIconsAdd3';

const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };
const DUPLICATE_RADIUS_METERS = 10;

// --- LOCAL MODULE CACHE FOR PERSISTENT TAB MEMORY ---
const DuplicateCheckOnImportCache = {
  activeItem: null,
  selectedDescPhotoIdx: 0,
  photoDescriptions: {},
  scrollPos: 0,
};

const normalizeLooseString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth radius in metres
  const p1 = lat1 * (Math.PI / 180);
  const p2 = lat2 * (Math.PI / 180);
  const dp = (lat2 - lat1) * (Math.PI / 180);
  const dl = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dp / 2) * Math.sin(dp / 2) +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) * Math.sin(dl / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default function DuplicateCheckOnImport({ stateStore }) {
  const state = stateStore?.state || {};
  const actions = stateStore?.actions || stateStore?.uiActions || {};
  const setters = stateStore?.setters || {};

  // AMENDED: Extract defaults from global state for the RTE component
  const rteFontType = state?.rteFontType || 'Helvetica';
  const rteFontSize = state?.rteFontSize || '10pt';

  const handleSelectRowGlobal = actions.handleSelectRow;
  const handleSelectAllRowsGlobal = actions.handleSelectAllRows;

  const setParsedDataGlobal = actions.setParsedData || setters.setParsedData;
  const setSelectedRowIdsGlobal = actions.setSelectedRowIds || setters.setSelectedRowIds;
  const setCheckedRightIdsGlobal =
    actions.setCheckedRightIds || setters.setCheckedRightIds;
  const setTriggerDuplicateDelete =
    actions.setTriggerDuplicateDelete || setters.setTriggerDuplicateDelete;
  const setPendingDuplicateDeletionsGlobal =
    actions.setPendingDuplicateDeletions || setters.setPendingDuplicateDeletions;

  const [localSelectedRowIds, setLocalSelectedRowIds] = useState([]);
  const [localCheckedRightIds, setLocalCheckedRightIds] = useState([]);

  const selectedRowIds = useMemo(() => {
    if (state.selectedRowIds && Array.isArray(state.selectedRowIds))
      return state.selectedRowIds;
    return localSelectedRowIds;
  }, [state.selectedRowIds, localSelectedRowIds]);

  const checkedRightIds = useMemo(() => {
    if (state.checkedRightIds && Array.isArray(state.checkedRightIds))
      return state.checkedRightIds;
    return localCheckedRightIds;
  }, [state.checkedRightIds, localCheckedRightIds]);

  const triggerDuplicateDelete = state.triggerDuplicateDelete || false;

  const unimportedData = useMemo(() => {
    const data = state?.parsedData || [];
    return data.filter((row) => !state?.successfullyImportedIds?.includes(row.id));
  }, [state?.parsedData, state?.successfullyImportedIds]);

  const makeUppercase =
    state?.settings?.textFormatting?.makeUppercase ?? state?.makeUppercase ?? false;

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // --- RESTORING ACTIVE STATE FROM CACHE ---
  const [activeItem, _setActiveItem] = useState(DuplicateCheckOnImportCache.activeItem);
  const setActiveItem = useCallback((val) => {
    _setActiveItem((prev) => {
      const next = typeof val === 'function' ? val(prev) : val;
      DuplicateCheckOnImportCache.activeItem = next;
      return next;
    });
  }, []);

  const [selectedDescPhotoIdx, _setSelectedDescPhotoIdx] = useState(
    DuplicateCheckOnImportCache.selectedDescPhotoIdx,
  );
  const setSelectedDescPhotoIdx = useCallback((val) => {
    _setSelectedDescPhotoIdx((prev) => {
      const next = typeof val === 'function' ? val(prev) : val;
      DuplicateCheckOnImportCache.selectedDescPhotoIdx = next;
      return next;
    });
  }, []);

  const [photoDescriptions, _setPhotoDescriptions] = useState(
    DuplicateCheckOnImportCache.photoDescriptions,
  );
  const setPhotoDescriptions = useCallback((val) => {
    _setPhotoDescriptions((prev) => {
      const next = typeof val === 'function' ? val(prev) : val;
      DuplicateCheckOnImportCache.photoDescriptions = next;
      return next;
    });
  }, []);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Prime the ref with the cached ID so we don't instantly wipe the cached descriptions on mount
  const prevDescRowIdRef = useRef(DuplicateCheckOnImportCache.activeItem?.id || null);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const leftScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const isSyncingLeftScroll = useRef(false);
  const isSyncingRightScroll = useRef(false);

  const userZoomLevel = state.userZoomLevel;
  const setUserZoomLevel = actions.setUserZoomLevel || setters.setUserZoomLevel;

  const userZoomRef = useRef(userZoomLevel);
  useEffect(() => {
    userZoomRef.current = userZoomLevel;
  }, [userZoomLevel]);

  // AMENDED: Stable ref for the zoom setter to avoid exhaustive-deps warnings
  const setUserZoomLevelRef = useRef(setUserZoomLevel);
  useEffect(() => {
    setUserZoomLevelRef.current = setUserZoomLevel;
  }, [setUserZoomLevel]);

  // Clear the cache completely if the global session resets (data cleared)
  useEffect(() => {
    if (!state?.parsedData || state.parsedData.length === 0) {
      DuplicateCheckOnImportCache.activeItem = null;
      DuplicateCheckOnImportCache.selectedDescPhotoIdx = 0;
      DuplicateCheckOnImportCache.photoDescriptions = {};
      DuplicateCheckOnImportCache.scrollPos = 0;
      _setActiveItem(null);
      _setSelectedDescPhotoIdx(0);
      _setPhotoDescriptions({});
      prevDescRowIdRef.current = null;
    }
  }, [state?.parsedData]);

  // Handle restoring the table scroll positions dynamically
  useEffect(() => {
    const restoreScroll = () => {
      if (leftScrollRef.current)
        leftScrollRef.current.scrollTop = DuplicateCheckOnImportCache.scrollPos;
      if (rightScrollRef.current)
        rightScrollRef.current.scrollTop = DuplicateCheckOnImportCache.scrollPos;
    };
    restoreScroll();
    setTimeout(restoreScroll, 10);
    setTimeout(restoreScroll, 100); // Delayed capture for table mounting lag
  }, []);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleToggleLeft = (id) => (e) => {
    e.stopPropagation();
    const current = selectedRowIds;
    const newIds = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];

    if (handleSelectRowGlobal) {
      handleSelectRowGlobal(id);
    } else if (setSelectedRowIdsGlobal) {
      setSelectedRowIdsGlobal(newIds);
    } else {
      setLocalSelectedRowIds(newIds);
    }
  };

  const handleToggleRight = (id) => (e) => {
    e.stopPropagation();
    const current = checkedRightIds;
    const newIds = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];

    if (setCheckedRightIdsGlobal) {
      setCheckedRightIdsGlobal(newIds);
    } else {
      setLocalCheckedRightIds(newIds);
    }
  };

  const handleSelectAllLeft = (e) => {
    const isChecked = e.target.checked;
    const newIds = isChecked ? processedRows.map((item) => item.row.id) : [];

    if (handleSelectAllRowsGlobal) {
      handleSelectAllRowsGlobal(e);
    } else if (setSelectedRowIdsGlobal) {
      setSelectedRowIdsGlobal(newIds);
    } else {
      setLocalSelectedRowIds(newIds);
    }
  };

  const handleSelectAllRight = (e) => {
    const isChecked = e.target.checked;
    const newIds = isChecked ? allRightIds : [];

    if (setCheckedRightIdsGlobal) {
      setCheckedRightIdsGlobal(newIds);
    } else {
      setLocalCheckedRightIds(newIds);
    }
  };

  const handleLeftScroll = (e) => {
    if (isSyncingLeftScroll.current) {
      isSyncingLeftScroll.current = false;
      return;
    }
    DuplicateCheckOnImportCache.scrollPos = e.target.scrollTop;
    if (rightScrollRef.current) {
      isSyncingRightScroll.current = true;
      rightScrollRef.current.scrollTop = e.target.scrollTop;
    }
  };

  const handleRightScroll = (e) => {
    if (isSyncingRightScroll.current) {
      isSyncingRightScroll.current = false;
      return;
    }
    DuplicateCheckOnImportCache.scrollPos = e.target.scrollTop;
    if (leftScrollRef.current) {
      isSyncingLeftScroll.current = true;
      leftScrollRef.current.scrollTop = e.target.scrollTop;
    }
  };

  const processedRows = useMemo(() => {
    const existingMarkers = window.__markerArray || [];

    const getRowTitle = (row) => {
      let titleVal = '';
      const titleColKey = Object.keys(state?.rowMappings || {}).find(
        (k) => state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
      );

      if (titleColKey) {
        let colName = titleColKey;
        const cRow = state.customRows?.find((r) => r.id === titleColKey);
        if (cRow && cRow.name !== '-') colName = cRow.name;
        else if (titleColKey.startsWith('prop_')) colName = titleColKey.substring(5);

        const actualKey = Object.keys(row).find(
          (k) => normalizeLooseString(k) === normalizeLooseString(colName),
        );
        if (actualKey) titleVal = row[actualKey];
      }
      if (!titleVal) {
        titleVal =
          row['Title'] ||
          row['title'] ||
          row['TITLE'] ||
          row['hoverText'] ||
          row['hoverContent'] ||
          'Untitled Marker';
      }
      return titleVal;
    };

    const getRowLatLng = (row) => {
      let latVal = null;
      let lngVal = null;

      const geocodeObj =
        state?.geocodedData?.[row.id] ||
        state?.geocodeResults?.[row.id] ||
        state?.geocodedPositions?.[row.id] ||
        state?.geocoded?.[row.id] ||
        row.geocodedData ||
        row.geocode;

      if (geocodeObj) {
        if (geocodeObj.lat !== undefined) latVal = geocodeObj.lat;
        if (geocodeObj.lng !== undefined) lngVal = geocodeObj.lng;
        if (geocodeObj.position?.lat !== undefined) latVal = geocodeObj.position.lat;
        if (geocodeObj.position?.lng !== undefined) lngVal = geocodeObj.position.lng;
      }

      if (latVal == null && row.position && row.position.lat !== undefined)
        latVal = row.position.lat;
      if (lngVal == null && row.position && row.position.lng !== undefined)
        lngVal = row.position.lng;

      if (latVal == null && row.lat !== undefined) latVal = row.lat;
      if (lngVal == null && row.lng !== undefined) lngVal = row.lng;
      if (latVal == null && row.latitude !== undefined) latVal = row.latitude;
      if (lngVal == null && row.longitude !== undefined) lngVal = row.longitude;

      if (latVal == null || lngVal == null) {
        const latColKey = Object.keys(state?.rowMappings || {}).find(
          (k) => state.rowMappings[k] === 'LAT',
        );
        const lngColKey = Object.keys(state?.rowMappings || {}).find(
          (k) => state.rowMappings[k] === 'LNG',
        );
        const posColKey = Object.keys(state?.rowMappings || {}).find(
          (k) =>
            state.rowMappings[k] === 'POSITION (LAT, LNG)' ||
            state.rowMappings[k] === 'POSITION',
        );

        const extractVal = (colKey, fallbacks) => {
          if (colKey) {
            let colName = colKey;
            const cRow = state.customRows?.find((r) => r.id === colKey);
            if (cRow && cRow.name !== '-') colName = cRow.name;
            else if (colKey.startsWith('prop_')) colName = colKey.substring(5);

            const actualKey = Object.keys(row).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            if (actualKey && row[actualKey] !== undefined && row[actualKey] !== null)
              return row[actualKey];
          }
          for (const fk of fallbacks) {
            const match = Object.keys(row).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(fk),
            );
            if (match && row[match] !== undefined && row[match] !== null)
              return row[match];
          }
          return null;
        };

        if (latVal == null) latVal = extractVal(latColKey, ['latitude', 'lat']);
        if (lngVal == null) lngVal = extractVal(lngColKey, ['longitude', 'lng']);

        if ((latVal == null || lngVal == null) && posColKey) {
          const combinedStr = extractVal(posColKey, [
            'position',
            'coords',
            'coordinates',
          ]);
          if (typeof combinedStr === 'string' && combinedStr.includes(',')) {
            const parts = combinedStr.split(',');
            latVal = parts[0].trim();
            lngVal = parts[1].trim();
          }
        }
      }

      const parsedLat = parseFloat(latVal);
      const parsedLng = parseFloat(lngVal);

      return {
        lat: !isNaN(parsedLat) ? parsedLat : null,
        lng: !isNaN(parsedLng) ? parsedLng : null,
      };
    };

    // Pre-calculate titles and coordinates for all unimported rows so we can cross-check them
    const precomputedRows = unimportedData.map((row) => {
      const rawTitle = getRowTitle(row);
      const displayTitle =
        makeUppercase && typeof rawTitle === 'string' ? rawTitle.toUpperCase() : rawTitle;
      const cellHasData =
        displayTitle !== undefined &&
        displayTitle !== null &&
        String(displayTitle).trim() !== '';

      const { lat, lng } = getRowLatLng(row);
      const hasCoords = lat !== null && lng !== null;

      return { row, displayTitle, cellHasData, hasCoords, lat, lng };
    });

    const mappedRows = precomputedRows.map((item, idx) => {
      const { row, displayTitle, cellHasData, hasCoords, lat, lng } = item;
      const dups = [];

      if (hasCoords) {
        // 1. Check against existing Firebase map markers
        existingMarkers.forEach((m) => {
          if (!m) return;

          // Skip if this marker belongs to a batch that is currently pending reinstatement
          const mImportBatchId = m.importBatchId || m.data?.importBatchId;
          if (
            mImportBatchId &&
            state?.pendingReinstateBatchIds?.includes(mImportBatchId)
          ) {
            return;
          }

          let mLat = null;
          let mLng = null;
          let mTitle = 'Untitled Marker';

          const tryExtractTitle = (obj) => {
            if (!obj) return null;
            if (typeof obj.getTitle === 'function') return obj.getTitle();
            return (
              obj.title || obj.TITLE || obj.hoverText || obj.hoverContent || obj.name
            );
          };

          mTitle =
            tryExtractTitle(m.data) ||
            tryExtractTitle(m.marker) ||
            tryExtractTitle(m) ||
            'Untitled Marker';

          const markerInstance = m.marker || m;
          const dataSource = m.data || m;

          if (typeof markerInstance.getPosition === 'function') {
            const pos = markerInstance.getPosition();
            if (pos) {
              mLat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
              mLng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
            }
          }

          if (mLat == null || mLng == null) {
            if (dataSource.position && dataSource.position.lat !== undefined) {
              mLat =
                typeof dataSource.position.lat === 'function'
                  ? dataSource.position.lat()
                  : dataSource.position.lat;
              mLng =
                typeof dataSource.position.lng === 'function'
                  ? dataSource.position.lng()
                  : dataSource.position.lng;
            } else {
              mLat = dataSource.lat !== undefined ? dataSource.lat : dataSource.latitude;
              mLng = dataSource.lng !== undefined ? dataSource.lng : dataSource.longitude;
            }
          }

          const parsedMLat = parseFloat(mLat);
          const parsedMLng = parseFloat(mLng);

          if (
            !isNaN(parsedMLat) &&
            !isNaN(parsedMLng) &&
            parsedMLat != null &&
            parsedMLng != null
          ) {
            const dist = getDistanceInMeters(lat, lng, parsedMLat, parsedMLng);

            if (dist <= DUPLICATE_RADIUS_METERS) {
              const mDisplayTitle =
                makeUppercase && typeof mTitle === 'string'
                  ? mTitle.toUpperCase()
                  : mTitle;
              dups.push({
                title: mDisplayTitle,
                distance: dist,
                originalMarker: m,
                lat: parsedMLat,
                lng: parsedMLng,
                isInternalRow: false,
              });
            }
          }
        });

        // 2. Check against other unimported rows in the current batch (Page 2 duplicates)
        precomputedRows.forEach((otherItem, otherIdx) => {
          if (idx === otherIdx || !otherItem.hasCoords) return;

          const dist = getDistanceInMeters(lat, lng, otherItem.lat, otherItem.lng);
          if (dist <= DUPLICATE_RADIUS_METERS) {
            dups.push({
              title: otherItem.cellHasData ? otherItem.displayTitle : 'Untitled Marker',
              distance: dist,
              originalMarker: otherItem.row,
              lat: otherItem.lat,
              lng: otherItem.lng,
              isInternalRow: true,
              internalRowId: otherItem.row.id,
            });
          }
        });

        dups.sort((a, b) => a.distance - b.distance);
      }

      const duplicates = dups.map((d, i) => ({ ...d, uniqueId: `${row.id}_dup_${i}` }));

      return { row, displayTitle, cellHasData, duplicates, hasCoords, lat, lng };
    });

    return mappedRows.filter((item) => item.hasCoords);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    unimportedData,
    makeUppercase,
    state?.rowMappings,
    state?.customRows,
    state?.rowSubMappings,
    refreshTrigger,
    state?.pendingReinstateBatchIds,
  ]);

  const allRightIds = processedRows.flatMap((pr) => pr.duplicates.map((d) => d.uniqueId));

  // --- GHOST ITEM CLEANUP: Drop activeItem if it's no longer in the list (e.g. deleted externally or all imported) ---
  useEffect(() => {
    if (activeItem) {
      if (processedRows.length === 0) {
        setActiveItem(null);
      } else {
        const stillExists = processedRows.some((pr) => {
          if (activeItem.panel === 'left') return pr.row.id === activeItem.id;
          return pr.duplicates.some((d) => d.uniqueId === activeItem.id);
        });
        if (!stillExists) {
          setActiveItem(null);
        }
      }
    }
  }, [processedRows, activeItem, setActiveItem]);

  // --- KEYBOARD & GLOBAL EVENTS FOR DELETION ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return;
      }
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        (checkedRightIds.length > 0 || selectedRowIds.length > 0)
      ) {
        if (e.key === 'Backspace') e.preventDefault();
        setConfirmOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [checkedRightIds, selectedRowIds]);

  // Handle the top toolbar button safely without batching interference
  useEffect(() => {
    if (triggerDuplicateDelete) {
      setConfirmOpen(true);
    }
  }, [triggerDuplicateDelete]);

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    if (setTriggerDuplicateDelete) setTriggerDuplicateDelete(false);
  };

  // --- DEFERRED LOGIC FOR GRID AND FIREBASE DELETION ---
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      const internalRowIdsFromRight = [];

      if (checkedRightIds.length > 0) {
        const markersToDelete = [];

        checkedRightIds.forEach((id) => {
          for (const pr of processedRows) {
            const match = pr.duplicates.find((d) => d.uniqueId === id);
            if (match) {
              if (match.isInternalRow && match.internalRowId) {
                internalRowIdsFromRight.push(match.internalRowId);
              } else if (match.originalMarker) {
                markersToDelete.push(match.originalMarker);
              }
              break;
            }
          }
        });

        if (markersToDelete.length > 0) {
          if (setPendingDuplicateDeletionsGlobal) {
            setPendingDuplicateDeletionsGlobal((prev) => [
              ...(prev || []),
              ...markersToDelete,
            ]);
          }

          // Clean up the object from the global array immediately so it vanishes from map/grid
          if (window.__markerArray) {
            window.__markerArray = window.__markerArray.filter(
              (existingObj) =>
                !markersToDelete.some(
                  (m) =>
                    m.id === existingObj.id ||
                    m.markerGroupID === existingObj.markerGroupID,
                ),
            );
          }
        }

        if (setCheckedRightIdsGlobal) setCheckedRightIdsGlobal([]);
        else setLocalCheckedRightIds([]);
      }

      const allRowIdsToDelete = Array.from(
        new Set([...selectedRowIds, ...internalRowIdsFromRight]),
      );

      if (allRowIdsToDelete.length > 0) {
        if (setParsedDataGlobal) {
          setParsedDataGlobal((prev) =>
            prev.filter((row) => !allRowIdsToDelete.includes(row.id)),
          );
        }

        if (setSelectedRowIdsGlobal) setSelectedRowIdsGlobal([]);
        else setLocalSelectedRowIds([]);
      }

      setConfirmOpen(false);
      if (setTriggerDuplicateDelete) setTriggerDuplicateDelete(false);

      if (activeItem) {
        if (activeItem.panel === 'right' && checkedRightIds.includes(activeItem.id))
          setActiveItem(null);
        if (activeItem.panel === 'left' && allRowIdsToDelete.includes(activeItem.id))
          setActiveItem(null);
      }

      handleRefresh();
    } catch (error) {
      console.error('Error executing delete setup:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const totalSelectedToDelete = selectedRowIds.length + checkedRightIds.length;
  let deleteMsg = `Do you want to permanently delete ${totalSelectedToDelete} marker${totalSelectedToDelete === 1 ? '' : 's'} and all ${totalSelectedToDelete === 1 ? 'its' : 'their'} information?`;

  // --- RESTORE ADVANCED MARKER ELEMENT MAP INITIALIZATION (WITH ASYNC IMPORT) ---
  useEffect(() => {
    if (!activeItem) return;

    const currentLat = parseFloat(activeItem.lat);
    const currentLng = parseFloat(activeItem.lng);

    if (isNaN(currentLat) || isNaN(currentLng)) return;
    const pos = { lat: currentLat, lng: currentLng };

    let isCancelled = false;

    const loadMap = async () => {
      if (!mapContainerRef.current || !window.google) return;

      try {
        if (!mapInstanceRef.current) {
          // Explicitly import the libraries dynamically just like GoogleMap.js
          const { Map } = await window.google.maps.importLibrary('maps');
          const { AdvancedMarkerElement } =
            await window.google.maps.importLibrary('marker');

          if (isCancelled) return;

          const initialZoom = userZoomRef.current !== null ? userZoomRef.current : 15;

          mapInstanceRef.current = new Map(mapContainerRef.current, {
            center: pos,
            zoom: initialZoom,
            mapId: process.env.REACT_APP_GOOGLE_MAP_ID,
            streetViewControl: false,
            mapTypeControl: false,
          });

          mapInstanceRef.current.addListener('zoom_changed', () => {
            if (setUserZoomLevelRef.current) {
              setUserZoomLevelRef.current(mapInstanceRef.current.getZoom());
            }
          });

          markerRef.current = new AdvancedMarkerElement({
            map: mapInstanceRef.current,
            position: pos,
          });
        } else {
          // Ensure resize is triggered if the map was previously hidden behind the overlay
          window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
          mapInstanceRef.current.panTo(pos);
          if (markerRef.current) {
            markerRef.current.position = pos;
          }
        }
      } catch (error) {
        console.error('Error loading DuplicateCheck map:', error);
      }
    };

    loadMap();

    return () => {
      isCancelled = true;
    };
  }, [activeItem]);

  // --- INITIALIZE PHOTOS AND DESC FOR ACTIVE ITEM ---
  useEffect(() => {
    if (activeItem?.id) {
      if (prevDescRowIdRef.current !== activeItem.id) {
        prevDescRowIdRef.current = activeItem.id;
        setSelectedDescPhotoIdx(0);

        const initialDescMap = {};
        const activeData = activeItem.data || {};
        const files = activeData.matchedFiles || activeData.photos || [];

        let hasAnyDescProp = false;
        if (files.length > 0) {
          files.forEach((file, idx) => {
            if (file.description !== undefined) {
              hasAnyDescProp = true;
              if (file.description) initialDescMap[idx] = file.description;
            }
          });
        }

        if (!hasAnyDescProp) {
          let descColName = null;
          const descColKey = Object.keys(state?.rowMappings || {}).find(
            (k) => state.rowMappings[k] === 'DESCRIPTION',
          );

          if (descColKey) {
            const cRow = state?.customRows?.find((r) => r.id === descColKey);
            if (cRow && cRow.name !== '-') descColName = cRow.name;
            else if (descColKey.startsWith('prop_'))
              descColName = descColKey.substring(5);
          }

          if (descColName) {
            const actualKey =
              Object.keys(activeData).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(descColName),
              ) || descColName;
            if (activeData[actualKey]) {
              initialDescMap[0] = activeData[actualKey];
            }
          }
        }
        setPhotoDescriptions(initialDescMap);
      }
    } else {
      prevDescRowIdRef.current = null;
    }
  }, [
    activeItem,
    state?.rowMappings,
    state?.customRows,
    setSelectedDescPhotoIdx,
    setPhotoDescriptions,
  ]);

  const activeData = activeItem?.data || {};
  const activeMatchedFiles = activeData.matchedFiles || activeData.photos || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
      }}>
      {/* --- TOP 50% OF THE SCREEN --- */}
      <Box
        sx={{
          height: '50%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}>
        {/* Global Headers (Fixed height within the Top 50%) */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '55px',
            minHeight: '55px',
            maxHeight: '55px',
            overflow: 'hidden',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              width: '50%',
              borderTop: '1px solid #ccc',
              borderRight: '1px solid #ccc',
              borderBottom: '1px solid #ccc',
              borderLeft: 'none',
              borderRadius: 0,
              overflow: 'hidden',
            }}>
            <Table size='small' sx={{ width: '100%' }}>
              <TableHead>
                <TableRow
                  sx={{
                    height: '55px',
                    '& th': {
                      backgroundColor: '#e8f5e9',
                      fontWeight: 'bold',
                      borderBottom: '1px solid #ccc',
                      py: '2px',
                      ...cellStyle,
                    },
                  }}>
                  <TableCell padding='checkbox' sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={
                        processedRows.length > 0 &&
                        processedRows.every((pr) => selectedRowIds.includes(pr.row.id))
                      }
                      indeterminate={
                        selectedRowIds.length > 0 &&
                        !processedRows.every((pr) => selectedRowIds.includes(pr.row.id))
                      }
                      onChange={handleSelectAllLeft}
                      size='small'
                      sx={{
                        p: 0.5,
                        '&.Mui-checked': { color: '#2e7d32' },
                        '&.MuiCheckbox-indeterminate': { color: '#2e7d32' },
                      }}
                    />
                  </TableCell>
                  <TableCell align='left' sx={{ paddingLeft: '16px', py: 0 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        minHeight: '20px',
                      }}>
                      Duplicate Marker Check
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              width: '50%',
              borderTop: '1px solid #ccc',
              borderBottom: '1px solid #ccc',
              borderLeft: 'none',
              borderRight: 'none',
              borderRadius: 0,
              overflow: 'hidden',
            }}>
            <Table size='small' sx={{ width: '100%' }}>
              <TableHead>
                <TableRow
                  sx={{
                    height: '55px',
                    '& th': {
                      backgroundColor: '#e8f5e9',
                      fontWeight: 'bold',
                      borderBottom: '1px solid #ccc',
                      py: '2px',
                      ...cellStyle,
                    },
                  }}>
                  <TableCell padding='checkbox' sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={
                        allRightIds.length > 0 &&
                        checkedRightIds.length === allRightIds.length
                      }
                      indeterminate={
                        checkedRightIds.length > 0 &&
                        checkedRightIds.length < allRightIds.length
                      }
                      onChange={handleSelectAllRight}
                      size='small'
                      sx={{
                        p: 0.5,
                        '&.Mui-checked': { color: '#2e7d32' },
                        '&.MuiCheckbox-indeterminate': { color: '#2e7d32' },
                      }}
                    />
                  </TableCell>
                  <TableCell align='left' sx={{ paddingLeft: '16px', py: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                      }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          minHeight: '20px',
                        }}>
                        Detected Duplicates
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        </Box>

        {/* Top Data Panels (Takes the remainder of the Top 50%) */}
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}>
          {/* Top Left Area */}
          <Box
            sx={{
              width: '50%',
              height: '100%',
              borderRight: '1px solid #ccc',
              borderBottom: '1px solid #ccc',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}>
            <TableContainer
              ref={leftScrollRef}
              onScroll={handleLeftScroll}
              sx={{ flexGrow: 1, overflowY: 'auto' }}>
              <Table size='small' sx={{ width: '100%' }}>
                <TableBody>
                  {processedRows.map(
                    ({ row, displayTitle, cellHasData, duplicates, lat, lng }) => {
                      const isChecked = selectedRowIds.includes(row.id);
                      const isActive =
                        activeItem?.id === row.id && activeItem?.panel === 'left';
                      const numRows = Math.max(1, duplicates.length);

                      const mainRow = (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() =>
                            setActiveItem({
                              id: row.id,
                              panel: 'left',
                              data: row,
                              lat,
                              lng,
                              displayTitle,
                            })
                          }
                          sx={{
                            height: '36px',
                            cursor: 'pointer',
                            bgcolor: isActive
                              ? 'rgba(0, 0, 0, 0.08) !important'
                              : 'inherit',
                          }}>
                          {/* Event propagation stopped here at the cell level to prevent row expansion */}
                          <TableCell
                            padding='checkbox'
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              borderBottom: '1px solid rgba(224, 224, 224, 1)',
                              py: '2px',
                            }}>
                            <Checkbox
                              checked={isChecked}
                              onChange={handleToggleLeft(row.id)}
                              onClick={(e) => e.stopPropagation()}
                              size='small'
                              sx={{ p: 0.5, '&.Mui-checked': { color: '#2e7d32' } }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid rgba(224, 224, 224, 1)',
                              py: '2px',
                              ...cellStyle,
                            }}>
                            <Typography
                              variant='body2'
                              sx={{
                                color: cellHasData ? '#333' : 'error.main',
                                fontStyle: cellHasData ? 'normal' : 'italic',
                              }}>
                              {cellHasData ? displayTitle : 'Enter Title'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );

                      const extraRows = Array.from({ length: numRows - 1 }).map(
                        (_, i) => (
                          <TableRow key={`${row.id}_empty_${i}`} sx={{ height: '36px' }}>
                            <TableCell
                              padding='checkbox'
                              sx={{
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                py: '2px',
                              }}>
                              <Checkbox
                                size='small'
                                sx={{ p: 0.5, visibility: 'hidden' }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                py: '2px',
                                ...cellStyle,
                              }}
                            />
                          </TableRow>
                        ),
                      );

                      return [mainRow, ...extraRows];
                    },
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Top Right Area */}
          <Box
            sx={{
              width: '50%',
              height: '100%',
              borderBottom: '1px solid #ccc',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}>
            <TableContainer
              ref={rightScrollRef}
              onScroll={handleRightScroll}
              sx={{ flexGrow: 1, overflowY: 'auto' }}>
              <Table size='small' sx={{ width: '100%' }}>
                <TableBody>
                  {allRightIds.length === 0 ? (
                    <TableRow sx={{ height: '36px' }}>
                      <TableCell
                        colSpan={2}
                        align='center'
                        sx={{ py: 4, borderBottom: 'none' }}>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ fontStyle: 'italic' }}>
                          no duplicates found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    processedRows.map(({ row, duplicates, hasCoords }) => {
                      if (!hasCoords || duplicates.length === 0) {
                        return (
                          <TableRow key={`empty_right_${row.id}`} sx={{ height: '36px' }}>
                            <TableCell
                              padding='checkbox'
                              sx={{
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                py: '2px',
                              }}>
                              <Checkbox
                                size='small'
                                sx={{ p: 0.5, visibility: 'hidden' }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                py: '2px',
                                ...cellStyle,
                              }}>
                              <Typography
                                variant='caption'
                                sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                no duplicates found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return duplicates.map((dup) => {
                        const isChecked = checkedRightIds.includes(dup.uniqueId);
                        const isActive =
                          activeItem?.id === dup.uniqueId &&
                          activeItem?.panel === 'right';

                        return (
                          <TableRow
                            key={dup.uniqueId}
                            hover
                            onClick={() =>
                              setActiveItem({
                                id: dup.uniqueId,
                                panel: 'right',
                                data: dup.originalMarker?.data || dup.originalMarker,
                                lat: dup.lat,
                                lng: dup.lng,
                                displayTitle: dup.title,
                              })
                            }
                            sx={{
                              height: '36px',
                              cursor: 'pointer',
                              bgcolor: isActive
                                ? 'rgba(0, 0, 0, 0.08) !important'
                                : 'inherit',
                            }}>
                            <TableCell
                              padding='checkbox'
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                py: '2px',
                              }}>
                              <Checkbox
                                checked={isChecked}
                                onChange={handleToggleRight(dup.uniqueId)}
                                onClick={(e) => e.stopPropagation()}
                                size='small'
                                sx={{ p: 0.5, '&.Mui-checked': { color: '#2e7d32' } }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                py: '2px',
                                ...cellStyle,
                              }}>
                              <Typography variant='body2'>{dup.title}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>

      {/* --- BOTTOM 50% OF THE SCREEN --- */}
      <Box
        sx={{ height: '50%', width: '100%', display: 'flex', boxSizing: 'border-box' }}>
        {/* Bottom Left Area (Google Map) */}
        <Box
          sx={{
            width: '50%',
            height: '100%',
            borderRight: '1px solid #ccc',
            boxSizing: 'border-box',
            position: 'relative',
          }}>
          {/* PERMANENT MAP MOUNT: Keeps the Google Maps Canvas alive behind the overlay */}
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

          {/* OVERLAY: Covers the map cleanly when no item is actively selected */}
          {!activeItem && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 10,
                backgroundColor: '#fff',
              }}>
              <Typography variant='body2' sx={{ color: 'grey', fontStyle: 'italic' }}>
                Select a row above to view location map
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bottom Right Area (Photos & RTE) */}
        <Box
          sx={{ width: '50%', height: '100%', boxSizing: 'border-box', display: 'flex' }}>
          {!activeItem ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography variant='body2' sx={{ color: 'grey', fontStyle: 'italic' }}>
                Select a row above to view photos and description
              </Typography>
            </Box>
          ) : (
            <>
              {/* 20% Photo Waterfall */}
              <Box
                sx={{
                  width: '20%',
                  height: '100%',
                  overflowY: 'auto',
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  borderRight: '1px solid #ccc',
                  backgroundColor: '#f5f5f5',
                }}>
                {activeMatchedFiles.length === 0 ? (
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'grey',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      mt: 2,
                    }}>
                    No media
                  </Typography>
                ) : (
                  activeMatchedFiles.map((file, idx) => {
                    const src = file.isGooglePhoto
                      ? file.sourceUrl
                      : file.preview ||
                        (file instanceof File
                          ? URL.createObjectURL(file)
                          : file.url || '');
                    const isSelected = selectedDescPhotoIdx === idx;
                    const isVideo =
                      file.type?.startsWith('video/') ||
                      (file.name && file.name.match(/\.(mp4|mov|avi|wmv|webm)$/i)) ||
                      file.type === 'video';
                    const isAudio =
                      file.type?.startsWith('audio/') ||
                      (file.name && file.name.match(/\.(mp3|wav|ogg|m4a)$/i)) ||
                      file.type === 'audio';

                    return (
                      <Box
                        key={idx}
                        onClick={() => setSelectedDescPhotoIdx(idx)}
                        sx={{
                          width: '100%',
                          aspectRatio: '1',
                          border: isSelected ? '3px solid #1976d2' : '1px solid #ccc',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          flexShrink: 0,
                          backgroundColor: '#fff',
                          display: 'flex',
                          '&:hover': { borderColor: isSelected ? '#1976d2' : '#999' },
                        }}>
                        {isVideo ? (
                          <video
                            src={`${src}#t=0.1`}
                            preload='metadata'
                            muted
                            playsInline
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : isAudio ? (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#f0f0f0',
                              p: 1,
                              boxSizing: 'border-box',
                            }}>
                            <SoundWaveIcon sx={{ fontSize: 40, mb: 1 }} />
                            <audio
                              src={src}
                              controls
                              style={{ width: '100%', height: '30px' }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Box>
                        ) : (
                          <img
                            src={src}
                            alt={`Ref ${idx}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* 80% Rich Text Editor */}
              <Box
                sx={{
                  width: '80%',
                  height: '100%',
                  overflowY: 'auto',
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <RichTextEditor
                  key={`rte-desc-${selectedDescPhotoIdx}-${activeItem?.id}`}
                  initialContent={photoDescriptions[selectedDescPhotoIdx] || ''}
                  readOnly={false}
                  defaultFontType={rteFontType} // AMENDED: Added dynamic props
                  defaultFontSize={rteFontSize} // AMENDED: Added dynamic props
                  placeholder={
                    <Typography
                      variant='caption'
                      sx={{ color: 'grey', fontStyle: 'italic' }}>
                      Description content...
                    </Typography>
                  }
                  onUpdate={(html) => {
                    setPhotoDescriptions((prev) => ({
                      ...prev,
                      [selectedDescPhotoIdx]: html,
                    }));
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* --- CONFIRMATION DIALOG --- */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        onConfirm={handleDeleteConfirm}
        message={deleteMsg}
        severity='error'
        confirmText='OK'
        cancelText='Cancel'
        hideCancel={isDeleting}
        hideConfirm={isDeleting}
        disableBackdropClick={isDeleting}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </Box>
  );
}
